import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TokenMetric, fetchTokenMetrics } from '../services/api';
import TokenAvatar from '../components/TokenAvatar';

export default function Home() {
  const [tokens, setTokens] = useState<TokenMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTokens = async () => {
      const tokenMetrics = await fetchTokenMetrics();
      // Sort by price change for trending section
      const sortedByPriceChange = [...tokenMetrics].sort((a, b) => {
        const aChange = a.price_change_24h ?? -Infinity;
        const bChange = b.price_change_24h ?? -Infinity;
        return bChange - aChange;
      });
      setTokens(sortedByPriceChange);
      setLoading(false);
    };
    loadTokens();
  }, []);

  const formatPriceChange = (change: number | undefined | null) => {
    if (change === undefined || change === null) return 'N/A';
    const formatted = change.toFixed(2);
    return `${change >= 0 ? '+' : ''}${formatted}%`;
  };

  // Sort tokens by total holders for the all coins section
  const tokensByHolders = [...tokens].sort((a, b) => {
    const aHolders = a.total_holders ?? 0;
    const bHolders = b.total_holders ?? 0;
    return bHolders - aHolders;
  });

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-4 py-6">
        {/* Compact Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-1">
            <span className="text-blue-500">PumperLens</span>
          </h1>
          <p className="text-gray-400 text-xs mb-3">
            Track trending coins on Solana
          </p>
          
          <div className="flex justify-center gap-2 mb-4">
            <Link 
              to="/signup" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-3 rounded text-xs transition-colors"
            >
              Sign Up
            </Link>
            <Link 
              to="/login" 
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-1.5 px-3 rounded text-xs transition-colors"
            >
              Login
            </Link>
          </div>
        </div>

        {/* Ultra Compact 3x3 Grid - Small and Centered */}
        <div className="flex justify-center">
          <div className="bg-gray-800 rounded-lg p-3 shadow-xl w-64">
            <h2 className="text-sm font-semibold mb-2 text-center">Trending Coins</h2>
            <div className="grid grid-cols-3 gap-1">
              {loading ? (
                Array.from({ length: 9 }).map((_, index) => (
                  <div key={index} className="bg-gray-700 p-1.5 rounded h-16 w-16">
                    <div className="flex flex-col items-center h-full justify-center">
                      <div className="h-3 w-3 bg-gray-600 rounded-full mb-1 animate-pulse"></div>
                      <div className="h-1 bg-gray-600 rounded w-6 animate-pulse"></div>
                    </div>
                  </div>
                ))
              ) : tokens.length > 0 ? (
                tokens.slice(0, 9).map((token) => (
                  <Link 
                    key={token.mint} 
                    to={`/token/${token.mint}`}
                    className="block bg-gray-700 p-1.5 rounded hover:bg-gray-600 transition-colors border border-gray-600 hover:border-gray-500 h-16 w-16"
                  >
                    <div className="flex flex-col items-center text-center h-full justify-center">
                      <TokenAvatar 
                        imageUrl={token.image_url || token.imageUrl}
                        symbol={token.symbol}
                        className="mb-1 flex-shrink-0"
                      />
                      <h3 className="text-xs font-bold text-blue-400 mb-0.5 truncate w-full leading-tight">{token.symbol || 'Unknown'}</h3>
                      <p className="text-gray-400 mb-0.5 leading-tight text-[10px]">
                        {token.total_holders ? (token.total_holders > 999 ? `${Math.floor(token.total_holders/1000)}k` : token.total_holders) : 'N/A'}
                      </p>
                      <p className={`font-medium leading-tight text-[10px] ${token.price_change_24h && token.price_change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {token.price_change_24h !== null && token.price_change_24h !== undefined ? 
                          `${token.price_change_24h >= 0 ? '+' : ''}${token.price_change_24h.toFixed(1)}%` : 'N/A'}
                      </p>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-3 text-center text-gray-400 py-4">
                  No token data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Coins Section - Scrollable Content Below */}
        <div className="mt-8 max-w-4xl mx-auto">
          <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
            <h2 className="text-md font-semibold mb-3 text-center">More Coins</h2>
            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
              {loading ? (
                Array.from({ length: 20 }).map((_, index) => (
                  <div key={index} className="bg-gray-700 p-2 rounded aspect-square">
                    <div className="flex flex-col items-center h-full justify-center">
                      <div className="h-4 w-4 bg-gray-600 rounded-full mb-1 animate-pulse"></div>
                      <div className="h-1.5 bg-gray-600 rounded w-6 animate-pulse"></div>
                    </div>
                  </div>
                ))
              ) : tokensByHolders.length > 9 ? (
                tokensByHolders.slice(9, 29).map((token) => (
                  <Link 
                    key={token.mint} 
                    to={`/token/${token.mint}`}
                    className="block bg-gray-700 p-2 rounded hover:bg-gray-600 transition-colors border border-gray-600 hover:border-gray-500 aspect-square"
                  >
                    <div className="flex flex-col items-center text-center h-full justify-center">
                      <TokenAvatar 
                        imageUrl={token.image_url || token.imageUrl}
                        symbol={token.symbol}
                        className="mb-1 flex-shrink-0"
                      />
                      <h3 className="text-xs font-medium text-blue-400 truncate w-full leading-tight">{token.symbol || 'Unknown'}</h3>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full text-center text-gray-400">
                  No additional coins available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Space for future content */}
        <div className="h-96"></div>
      </div>
    </main>
  );
} 